// Catálogo de prêmios do baú de recompensas (admin): tabela, modal e switch de ativo.
// A chance é recalculada localmente (effectiveChances) a cada mudança na lista.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import {
  createRewardDefinition,
  listRewardDefinitions,
  updateRewardDefinition,
  type RewardDefinition,
  type RewardDefinitionPayload,
  type RewardRarity,
} from '../../services/admin';
import { apiErrorMessage } from './desafios';
import { RecompensasMarcos } from './RecompensasMarcos';
import { RARITY_TAG, effectiveChances, formatChance, rewardLabel } from './recompensas';

const SAVE_ERROR = 'Falha ao salvar o prêmio.';

const toPayload = (d: RewardDefinition | RewardDefinitionPayload): RewardDefinitionPayload => ({
  name: d.name,
  rarity: d.rarity,
  // InputNumber limpo entrega null; a API espera número.
  weight: d.weight ?? 0,
  active: d.active,
  hintCards: d.hintCards ?? 0,
  streakProtectors: d.streakProtectors ?? 0,
});

const NEW_DEFAULTS: RewardDefinitionPayload = {
  name: '',
  rarity: 'common',
  weight: 1,
  active: true,
  hintCards: 1,
  streakProtectors: 0,
};

export function RecompensasCatalog(): JSX.Element {
  const [defs, setDefs] = useState<RewardDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<RewardDefinition | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
  const [form] = Form.useForm<RewardDefinitionPayload>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDefs(await listRewardDefinitions());
      setLoaded(true);
    } catch {
      message.error('Falha ao carregar os prêmios.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const chances = useMemo(() => effectiveChances(defs), [defs]);
  const activeWeight = useMemo(
    () => defs.reduce((sum, d) => (d.active ? sum + d.weight : sum), 0),
    [defs],
  );

  const toggleActive = async (d: RewardDefinition, active: boolean): Promise<void> => {
    const apply = (value: boolean): void =>
      setDefs((list) => list.map((x) => (x.id === d.id ? { ...x, active: value } : x)));
    const setPending = (on: boolean): void =>
      setPendingIds((prev) => {
        const next = new Set(prev);
        if (on) next.add(d.id);
        else next.delete(d.id);
        return next;
      });
    setPending(true);
    apply(active);
    try {
      await updateRewardDefinition(d.id, { ...toPayload(d), active });
    } catch (err: unknown) {
      apply(d.active);
      message.error(apiErrorMessage(err) ?? SAVE_ERROR);
    } finally {
      setPending(false);
    }
  };

  const openCreate = (): void => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue(NEW_DEFAULTS);
    setFormOpen(true);
  };

  const openEdit = (d: RewardDefinition): void => {
    setEditing(d);
    form.resetFields();
    form.setFieldsValue(toPayload(d));
    setFormOpen(true);
  };

  const handleSubmit = async (): Promise<void> => {
    let v: RewardDefinitionPayload;
    try {
      v = await form.validateFields();
    } catch {
      return;
    }
    const payload = toPayload(v);
    setSaving(true);
    try {
      if (editing) await updateRewardDefinition(editing.id, payload);
      else await createRewardDefinition(payload);
      message.success('Prêmio salvo.');
      setFormOpen(false);
      await load();
    } catch (err: unknown) {
      message.error(apiErrorMessage(err) ?? SAVE_ERROR);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Nome', dataIndex: 'name' },
    {
      title: 'Raridade',
      dataIndex: 'rarity',
      render: (r: RewardRarity) => <Tag color={RARITY_TAG[r].color}>{RARITY_TAG[r].label}</Tag>,
    },
    { title: 'Conteúdo', key: 'content', render: (_: unknown, d: RewardDefinition) => rewardLabel(d) },
    { title: 'Peso', dataIndex: 'weight' },
    {
      title: 'Chance',
      key: 'chance',
      render: (_: unknown, d: RewardDefinition) => formatChance(chances[d.id] ?? 0),
    },
    {
      title: 'Ativo',
      key: 'active',
      render: (_: unknown, d: RewardDefinition) => (
        <Switch
          checked={d.active}
          loading={pendingIds.has(d.id)}
          disabled={pendingIds.has(d.id)}
          aria-label={`Ativo: ${d.name}`}
          onChange={(checked) => toggleActive(d, checked)}
        />
      ),
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_: unknown, d: RewardDefinition) => (
        <Button size="small" onClick={() => openEdit(d)}>
          Editar
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Recompensas</h2>
        <Button type="primary" onClick={openCreate}>
          Novo prêmio
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={defs}
        pagination={false}
      />

      <p style={{ marginTop: 12 }}>{`Soma dos pesos ativos: ${activeWeight}`}</p>
      {loaded && activeWeight === 0 && (
        <Alert type="warning" message="Sem prêmio ativo: o baú dá 1 dica." />
      )}

      <h3 style={{ marginTop: 32 }}>Marcos</h3>
      <RecompensasMarcos />

      <Modal
        title={editing ? `Editar prêmio #${editing.id}` : 'Novo prêmio'}
        visible={formOpen}
        onOk={handleSubmit}
        onCancel={() => setFormOpen(false)}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false} initialValues={NEW_DEFAULTS}>
          <Form.Item
            name="name"
            label="Nome"
            rules={[{ required: true, whitespace: true, message: 'Informe o nome.' }]}
          >
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item name="rarity" label="Raridade" rules={[{ required: true }]}>
            <Select
              aria-label="Raridade"
              options={(Object.keys(RARITY_TAG) as RewardRarity[]).map((r) => ({
                value: r,
                label: RARITY_TAG[r].label,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="weight"
            label="Peso"
            rules={[
              { required: true, message: 'Informe o peso.' },
              { type: 'number', min: 0, message: 'Informe um peso válido.' },
            ]}
          >
            <InputNumber min={0} max={1000} precision={0} />
          </Form.Item>
          <Form.Item name="hintCards" label="Dicas">
            <InputNumber min={0} max={1000} precision={0} />
          </Form.Item>
          <Form.Item
            name="streakProtectors"
            label="Protetores"
            dependencies={['hintCards']}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_rule, value) =>
                  (getFieldValue('hintCards') ?? 0) + (value ?? 0) > 0
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error('O prêmio precisa dar pelo menos uma dica ou um protetor.'),
                      ),
              }),
            ]}
          >
            <InputNumber min={0} max={1000} precision={0} />
          </Form.Item>
          <Form.Item name="active" label="Ativo" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default RecompensasCatalog;
